import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import "@/lib/sentry-deferred"; // Deferred Sentry initialization to reduce initial bundle
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import AccessibilitySkipLinks from "@/components/AccessibilitySkipLinks";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";

import Analytics from "./components/Analytics";
import MetaPixel from "./components/MetaPixel";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useErrorLogger } from "@/components/ErrorLogger";

import { BottomNavigation } from "@/components/BottomNavigation";
import { GlobalScreenReaderAnnouncer } from "@/components/accessibility/ScreenReaderAnnouncer";
import ScrollToTop from "@/components/ScrollToTop";
import { WebVitalsTracker } from "@/components/WebVitalsTracker";
import NavigationProgress from "@/components/NavigationProgress";
import { IntelligentPrefetch } from "@/components/IntelligentPrefetch";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { RoleBasedRoute } from "./components/RoleBasedRoute";
import { RedirectRoute } from "./components/RedirectRoute";
import { Suspense, lazy } from "react";
import { Skeleton } from "./components/ui/skeleton";

const LazyCookieBanner = lazy(() => import("@/components/CookieBanner"));

// Only Index and NotFound are eagerly loaded - everything else is lazy
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
const LazyOAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const LazyLogin = lazy(() => import("./pages/Login"));


// All other pages are lazy loaded for optimal code splitting
import {
  LazyBlogList,
  LazyBlogEditor,
  LazyCategoryManager,
  LazyTagManager,
  LazyBlogAnalytics,
  
  LazyDashboardOverview,
  LazyRoleManagement,
  LazySEOPerformance,
  LazyImageAudit,
  LazyImageOptimizationGuide,
  LazyMetaPixelAnalytics,
  LazyDocumentManager,
  LazyHeroPageManager,
  LazyHomeWallManager,
  LazyAdminHome,
  LazyProfilo,
  LazyImpostazioni,
  LazySEOManager,
  LazySEOPageEditor,
  LazyRedirectManager,
  LazySEOAudit,
  LazyFabbriche,
  LazyFabbricaPage,
  LazyMarchioPage,
  LazyGruppo,
  LazyMarchi,
  LazyCertificazioni,
  LazyProgetti,
  LazyRivista,
  LazyNotizie,
  LazyContatti,
  LazySitemap,
  LazyArticoloDetail,
  LazyResetPassword,
  LazyPrivacy,
  LazyCookiePolicy,
  LazyTerminiServizio,
  LazyLandingPageList,
  LazyLandingPageEditor,
  LazyLandingPageAnalytics,
  LazyLandingPageView,
  LazyEventManager,
  LazyEventEditor,
  LazyPressKitManager,
  LazyMediaLibrary,
  LazyFabbricheManager,
  LazyFabbricaEditor,
  LazyMarchiManager,
  LazyMarchioEditor,
  LazyCategorieManager2,
  LazyServiziManager,
  LazyServizioEditor,
  LazyServizioPage,
  LazyShop,
  LazyShopProdotto,
  LazyShopMarchio,
  LazyShopFeaturedManager,
  LazyEmporioMilitare,
  PageSkeleton,
} from "./components/LazyComponents";

// Sentry is now initialized after page load via sentry-deferred.ts

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
    },
  },
});

const AppContent = () => {
  useErrorLogger();
  

  // Note: extension unhandledrejection suppression lives in main.tsx (runs earlier).

  return (
    <BrowserRouter>
      <AccessibilitySkipLinks />
      <ScrollToTop />
      <NavigationProgress />
      <IntelligentPrefetch />
      <Analytics />
      <MetaPixel />
      <BottomNavigation />
      <LocalBusinessSchema />
      <Suspense fallback={null}><LazyCookieBanner /></Suspense>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Suspense fallback={<PageSkeleton />}><LazyLogin /></Suspense>} />
              <Route path="/.lovable/oauth/consent" element={<Suspense fallback={<PageSkeleton />}><LazyOAuthConsent /></Suspense>} />

              <Route path="/privacy" element={<Suspense fallback={<PageSkeleton />}><LazyPrivacy /></Suspense>} />
              <Route path="/cookie-policy" element={<Suspense fallback={<PageSkeleton />}><LazyCookiePolicy /></Suspense>} />
              <Route path="/termini-servizio" element={<Suspense fallback={<PageSkeleton />}><LazyTerminiServizio /></Suspense>} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}><LazyDashboardOverview /></Suspense>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyAdminHome /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/roles" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyRoleManagement /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/meta-pixel" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyMetaPixelAnalytics /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/documents" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="admin">
                    <Suspense fallback={<PageSkeleton />}><LazyDocumentManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/hero-pagine" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyHeroPageManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/shop-evidenza" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="admin">
                    <Suspense fallback={<PageSkeleton />}><LazyShopFeaturedManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/wall-home" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyHomeWallManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/seo" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazySEOManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/seo/:pagePath" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazySEOPageEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/redirects" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyRedirectManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin/seo-audit" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazySEOAudit /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyBlogList /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/new" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyBlogEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/edit/:id" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyBlogEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/categories" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyCategoryManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/tags" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyTagManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/analytics" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyBlogAnalytics /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/seo" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}><LazySEOPerformance /></Suspense>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/image-audit" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}><LazyImageAudit /></Suspense>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/image-guide" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSkeleton />}><LazyImageOptimizationGuide /></Suspense>
                </ProtectedRoute>
              } />
              <Route path="/brands/:slug" element={<Suspense fallback={<PageSkeleton />}><LazyMarchioPage /></Suspense>} />

              <Route path="/fabbriche" element={<Suspense fallback={<PageSkeleton />}><LazyFabbriche /></Suspense>} />
              <Route path="/fabbriche/:slug" element={<Suspense fallback={<PageSkeleton />}><LazyFabbricaPage /></Suspense>} />

              <Route path="/gruppo" element={<Suspense fallback={<PageSkeleton />}><LazyGruppo /></Suspense>} />
              <Route path="/marchi" element={<Suspense fallback={<PageSkeleton />}><LazyMarchi /></Suspense>} />
              <Route path="/progetti" element={<Suspense fallback={<PageSkeleton />}><LazyProgetti /></Suspense>} />
              <Route path="/progetti/:slug" element={<Suspense fallback={<PageSkeleton />}><LazyServizioPage /></Suspense>} />
              <Route path="/certificazioni" element={<Suspense fallback={<PageSkeleton />}><LazyCertificazioni /></Suspense>} />
              <Route path="/notizie/blog" element={<Suspense fallback={<PageSkeleton />}><LazyRivista /></Suspense>} />
              <Route path="/notizie/blog/:slug" element={<Suspense fallback={<PageSkeleton />}><LazyArticoloDetail /></Suspense>} />
              <Route path="/notizie" element={<Suspense fallback={<PageSkeleton />}><LazyNotizie /></Suspense>} />
              <Route path="/mappa-sito" element={<Suspense fallback={<PageSkeleton />}><LazySitemap /></Suspense>} />
              <Route path="/sitemap-page" element={<RedirectRoute to="/mappa-sito" />} />
              {/* Legacy redirects (301-style) */}
              <Route path="/marchi/:slug" element={<RedirectRoute to="/brands/:slug" />} />
              <Route path="/catalogo" element={<RedirectRoute to="/marchi" />} />
              <Route path="/blog" element={<RedirectRoute to="/notizie/blog" />} />
              <Route path="/blog/:slug" element={<RedirectRoute to="/notizie/blog/:slug" />} />
              <Route path="/rivista" element={<RedirectRoute to="/notizie/blog" />} />
              <Route path="/eventi" element={<RedirectRoute to="/notizie" />} />
              <Route path="/contatti" element={<Suspense fallback={<PageSkeleton />}><LazyContatti /></Suspense>} />
              <Route path="/auth/reset" element={<Suspense fallback={<PageSkeleton />}><LazyResetPassword /></Suspense>} />
              <Route path="/profilo" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><LazyProfilo /></Suspense></ProtectedRoute>} />
              <Route path="/impostazioni" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><LazyImpostazioni /></Suspense></ProtectedRoute>} />
              
              {/* Landing Pages - Public routes */}
              <Route path="/scan/:code" element={<Suspense fallback={<PageSkeleton />}><LazyLandingPageView /></Suspense>} />
              <Route path="/s/:shortCode" element={<Suspense fallback={<PageSkeleton />}><LazyLandingPageView /></Suspense>} />
              
              {/* Landing Pages - Admin routes */}
              <Route path="/dashboard/landing-pages" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyLandingPageList /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/landing-pages/new" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyLandingPageEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/landing-pages/edit/:id" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyLandingPageEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/landing-pages/analytics" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyLandingPageAnalytics /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Events Management */}
              <Route path="/dashboard/eventi" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyEventManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/eventi/new" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyEventEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/eventi/edit/:id" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyEventEditor /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Press Kit Management */}
              <Route path="/dashboard/press-kit" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="admin">
                    <Suspense fallback={<PageSkeleton />}><LazyPressKitManager /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Media Library */}
              <Route path="/dashboard/media" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="blog_editor">
                    <Suspense fallback={<PageSkeleton />}><LazyMediaLibrary /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />
              
              {/* Entity CMS Management */}
              <Route path="/dashboard/entita/fabbriche" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyFabbricheManager /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/fabbriche/new" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyFabbricaEditor /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/fabbriche/edit/:id" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyFabbricaEditor /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/marchi" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyMarchiManager /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/marchi/new" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyMarchioEditor /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/marchi/edit/:id" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyMarchioEditor /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/categorie" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyCategorieManager2 /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/servizi" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyServiziManager /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/servizi/new" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyServizioEditor /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              <Route path="/dashboard/entita/servizi/edit/:id" element={
                <ProtectedRoute><RoleBasedRoute requiredRole="blog_editor">
                  <Suspense fallback={<PageSkeleton />}><LazyServizioEditor /></Suspense>
                </RoleBasedRoute></ProtectedRoute>
              } />
              
              
              {/* Shop & Emporio Militare */}
              <Route path="/shop" element={<Suspense fallback={<PageSkeleton />}><LazyShop /></Suspense>} />
              <Route path="/shop/marchio/:slug" element={<Suspense fallback={<PageSkeleton />}><LazyShopMarchio /></Suspense>} />
              <Route path="/shop/prodotto/:slug" element={<Suspense fallback={<PageSkeleton />}><LazyShopProdotto /></Suspense>} />
              <Route path="/emporio-militare" element={
                <ProtectedRoute>
                  <RoleBasedRoute requiredRole="membro_forze_armate">
                    <Suspense fallback={<PageSkeleton />}><LazyEmporioMilitare /></Suspense>
                  </RoleBasedRoute>
                </ProtectedRoute>
              } />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
      </BrowserRouter>
    );
  };

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <ErrorBoundary>
              <WebVitalsTracker />
              <GlobalScreenReaderAnnouncer />
              <Toaster />
              <Sonner />
              <CartProvider>
                <AppContent />
              </CartProvider>
            </ErrorBoundary>
          </TooltipProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
